import type { ModuleId, UserRole } from '../types';

/**
 * Static metadata for every platform module. The launcher, sidebar and route
 * gating all read from this — adding a module here is step one of wiring it in.
 */
export interface ModuleMeta {
  id: ModuleId;
  name: string;
  /** One-line description shown on the launcher card */
  blurb: string;
  /** Path the launcher card opens */
  homePath: string;
  /** Roles that can see/open the module at all */
  roles: UserRole[];
  /** Tailwind classes for the launcher card accent */
  accentBg: string;
  accentText: string;
  /** Emoji icon — consistent with the rest of the UI's lightweight icon style */
  icon: string;
}

export const MODULES: ModuleMeta[] = [
  {
    id: 'pm',
    name: 'Performance Management',
    blurb: 'Run your whole performance management cycle — self-reviews, line manager sign-off and mid-year check-ins.',
    homePath: '/todo',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-blue-50',
    accentText: 'text-blue-700',
    icon: '📋',
  },
  {
    id: 'cpd',
    name: 'CPD Log',
    blurb: 'Staff record their professional development through the year, with attachments and leadership oversight.',
    homePath: '/cpd',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-emerald-50',
    accentText: 'text-emerald-700',
    icon: '🎓',
  },
  {
    id: 'marker',
    name: 'Marking',
    blurb: 'Feedback slips from comment banks and assessment gap analysis — with AI to do the heavy lifting.',
    homePath: '/marker',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-violet-50',
    accentText: 'text-violet-700',
    icon: '✏️',
  },
  {
    id: 'insights',
    name: 'Assessment Insights',
    blurb: 'School-wide assessment analytics for leadership — averages, band profiles and class comparisons without a single spreadsheet.',
    homePath: '/insights',
    // Senior leadership only — must match the route guard in App.tsx and
    // LEADERSHIP_ROLES in functions/src/insights (line managers are excluded).
    roles: ['schoolAdmin', 'headteacher'],
    accentBg: 'bg-sky-50',
    accentText: 'text-sky-700',
    icon: '📊',
  },
  {
    id: 'policies',
    name: 'Policies',
    blurb: 'Every school policy in one register — owners, ratification, review dates and proof that staff have read what they must.',
    homePath: '/policies',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-rose-50',
    accentText: 'text-rose-700',
    icon: '📜',
  },
  {
    id: 'wellbeing',
    name: 'Wellbeing',
    blurb: 'A weekly thirty-second staff pulse. Honest, anonymous, and leadership see the trends before they become problems.',
    homePath: '/wellbeing',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-teal-50',
    accentText: 'text-teal-700',
    icon: '💙',
  },
  {
    id: 'leave',
    name: 'Leave & Cover',
    blurb: 'Staff book leave against your own school day; it routes to their line manager then SLT, and the cover team sees exactly which periods need covering — and nothing else.',
    homePath: '/leave',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-amber-50',
    accentText: 'text-amber-700',
    icon: '🗓️',
  },
  {
    id: 'behaviour',
    name: 'Behaviour & Pastoral',
    blurb: 'Log behaviour and rewards, run Head Teacher detentions and the isolation booth, Out Of Lesson, one-way breaches and morning lates, raise on-call alerts — all on the school roster.',
    homePath: '/behaviour/detentions',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-rose-50',
    accentText: 'text-rose-700',
    icon: '🧭',
  },
  {
    id: 'send',
    name: 'SEND & EHCP',
    blurb: 'The SEND register with every review date, a one-line support summary each teacher can act on, and proof that the provision a plan specifies is actually being delivered.',
    // Most staff only ever see their own pupils' support summaries, so that is
    // where the launcher card lands rather than the register they cannot read.
    homePath: '/send/passports',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-lime-50',
    accentText: 'text-lime-700',
    icon: '🧠',
  },
  {
    id: 'staffAbsence',
    name: 'Staff Absence',
    blurb: 'Bradford scores and an absence calendar straight from your MIS — for the few people the school names to see them, and nobody else.',
    homePath: '/staff-absence',
    // Every role, because who may see a Bradford score is a list the school
    // admin keeps in staffAbsenceConfig, not a role — the page explains itself
    // to anyone not on it. Same shape as /leave/cover.
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff'],
    accentBg: 'bg-orange-50',
    accentText: 'text-orange-700',
    icon: '🩺',
  },
  {
    id: 'options',
    name: 'KS4 Options',
    blurb: 'Run GCSE options online: set the blocks and subjects, pupils make their choices, and leadership see demand against capacity live.',
    homePath: '/options',
    roles: ['schoolAdmin', 'headteacher', 'lineManager', 'staff', 'student'],
    accentBg: 'bg-indigo-50',
    accentText: 'text-indigo-700',
    icon: '🧩',
  },
];

export const MODULE_BY_ID: Record<ModuleId, ModuleMeta> = Object.fromEntries(
  MODULES.map((m) => [m.id, m]),
) as Record<ModuleId, ModuleMeta>;
